import type { AssistantCell } from "./assistant-format.ts";
import { assistantOptionalBlockTypes, type AssistantChartBlock, type AssistantChartKind, type AssistantColumn, type AssistantOptionalBlockType, type AssistantTableBlock } from "./schemas/assistant.ts";
import { MONTH_SHORT } from "./time.ts";

/**
 * Charts of the AI Assistant (strategy repo `teamna/strategy/ai-assistant-generic-data.md`,
 * decisions A4 to A7). Two parts, both pure:
 *
 * - `pickAssistantChart`: the rules that choose a chart for a result (A7). The model may ask for a
 *   view; the rules keep only a view that fits the shape of the data.
 * - `assistantChartModel`: a chart block and its table as bars, lines or tiles to draw. Desk,
 *   Control Center and the mobile app draw this model and never count, add or round.
 */

export type AssistantChartView = "auto" | "table" | AssistantChartKind;

/** Names on bars across: more is a table. */
export const CHART_BARS_MAX = 30;
/** Time values as bars up: more is a line, when the view is auto. */
export const CHART_TIME_BARS_MAX = 16;
/** Points on a line: more is a table. */
export const CHART_LINE_MAX = 120;
/** Parts of a stacked bar: more is a table. */
export const CHART_STACK_MAX = 8;
/** KPI tiles. */
export const CHART_TILES_MAX = 4;

export type ChartPickInput = {
  view: AssistantChartView;
  /** The group columns of the table, in order. `time` for day, week and month (a weekday is a name). */
  groups: Array<{ key: string; time: boolean }>;
  /** The metric columns, without the columns of the period before. */
  metrics: Array<{ key: string; kind: AssistantColumn["kind"] }>;
  /** Metric key to the column of the period before, when the question compares. */
  previous: Record<string, string> | null;
  rows: number;
  /** Values of the second group, for stacked bars. */
  stackValues: number;
  partialLast: boolean;
};

/** The chart for a result, or null for a table only. */
export function pickAssistantChart(input: ChartPickInput): Omit<AssistantChartBlock, "type"> | null {
  if (input.view === "table" || input.metrics.length === 0) return null;
  const first = input.metrics[0]!;
  const sameKind = input.metrics.filter((metric) => metric.kind === first.kind).map((metric) => metric.key);
  const base = { stack: null, time: false, partialLast: false };

  if (input.groups.length === 0) {
    return { ...base, kind: "kpi", x: null, series: input.metrics.slice(0, CHART_TILES_MAX).map((metric) => metric.key) };
  }
  if (input.rows === 0) return null;

  if (input.groups.length === 1) {
    const group = input.groups[0]!;
    const before = input.previous?.[first.key];
    // With the period before: the main metric and its value before, side by side.
    const series = before ? [first.key, before] : sameKind.slice(0, 4);
    if (group.time) {
      const time = { ...base, x: group.key, series, time: true, partialLast: input.partialLast };
      const line = input.view === "line" || (input.view !== "bar" && input.rows > CHART_TIME_BARS_MAX);
      if (line) return input.rows <= CHART_LINE_MAX ? { ...time, kind: "line" } : null;
      return input.rows <= CHART_LINE_MAX ? { ...time, kind: "bar" } : null;
    }
    return input.rows <= CHART_BARS_MAX ? { ...base, kind: "bar", x: group.key, series } : null;
  }

  // Two groups: stacked bars of one count or amount. Rates do not add up, so they stay a table.
  if (first.kind === "percent" || input.stackValues > CHART_STACK_MAX) return null;
  const [x, stack] = input.groups as [ChartPickInput["groups"][number], ChartPickInput["groups"][number]];
  const bars = Math.ceil(input.rows / Math.max(input.stackValues, 1));
  if (bars > (x.time ? CHART_LINE_MAX : CHART_BARS_MAX)) return null;
  return { kind: "stacked_bar", x: x.key, stack: stack.key, series: [first.key], time: x.time, partialLast: x.time && input.partialLast };
}

// ---------------------------------------------------------------------------
// The model to draw
// ---------------------------------------------------------------------------

export type ChartAxisValue = { label: string; partial: boolean };
export type ChartSeries = { key: string; label: string; kind: AssistantColumn["kind"]; values: Array<number | null> };
export type ChartTile = { key: string; label: string; kind: AssistantColumn["kind"]; value: AssistantCell; previous: AssistantCell; change: AssistantCell };

export type AssistantChartModel =
  | { kind: "bar" | "line" | "stacked_bar"; across: boolean; axis: ChartAxisValue[]; series: ChartSeries[]; max: number; kindOfValues: AssistantColumn["kind"] }
  | { kind: "kpi"; tiles: ChartTile[] };

/** A short label for the axis: "5 Sep" for a day, "Sep" for "September 2026". */
export function chartAxisLabel(value: AssistantCell): string {
  const text = value === null ? "" : String(value);
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (day) return `${Number(day[3])} ${MONTH_SHORT[Number(day[2]) - 1]}`;
  const month = /^([A-Z][a-z]+) \d{4}$/.exec(text);
  return month ? month[1]!.slice(0, 3) : text;
}

/** Tops of the scale, times a power of ten: close to the largest value, so the bars fill the space. */
const SCALE_STEPS = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

/** A top of the scale that reads well, at least the largest value. */
export function chartScaleMax(largest: number): number {
  if (largest <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(largest));
  const step = SCALE_STEPS.find((factor) => factor * power >= largest)!;
  return Math.round(step * power * 1000) / 1000;
}

function numberOf(value: AssistantCell | undefined): number | null {
  return typeof value === "number" ? value : null;
}

/**
 * The chart of a table, or null when the block does not fit the table (a column is missing): the
 * screen then shows the table alone.
 */
export function assistantChartModel(chart: AssistantChartBlock, table: AssistantTableBlock): AssistantChartModel | null {
  const column = (key: string | null) => (key ? table.columns.find((item) => item.key === key) : undefined);
  const series = chart.series.map(column);
  if (series.some((item) => !item)) return null;
  const columns = series as AssistantColumn[];

  if (chart.kind === "kpi") {
    const row = table.rows[0];
    if (!row) return null;
    return {
      kind: "kpi",
      tiles: columns.map((item) => ({
        key: item.key,
        label: item.label,
        kind: item.kind,
        value: row[item.key] ?? null,
        previous: row[`${item.key}__previous`] ?? null,
        change: row[`${item.key}__change`] ?? null,
      })),
    };
  }

  const x = column(chart.x);
  if (!x) return null;
  const across = !chart.time;
  const kindOfValues = columns[0]!.kind;

  if (chart.kind === "stacked_bar") {
    const stack = column(chart.stack);
    if (!stack) return null;
    const metric = columns[0]!;
    const axisKeys: string[] = [];
    const parts: string[] = [];
    const cells = new Map<string, number | null>();
    for (const row of table.rows) {
      const at = String(row[x.key] ?? "");
      const part = String(row[stack.key] ?? "");
      if (!axisKeys.includes(at)) axisKeys.push(at);
      if (!parts.includes(part)) parts.push(part);
      // The table holds names, not ids: two people with the same name on one bar cannot be told
      // apart, so no chart (the table still shows both rows).
      if (cells.has(`${at}\u0000${part}`)) return null;
      cells.set(`${at}\u0000${part}`, numberOf(row[metric.key]));
    }
    const stacked = parts.map((part) => ({ key: part, label: part, kind: metric.kind, values: axisKeys.map((at) => cells.get(`${at}\u0000${part}`) ?? null) }));
    const largest = Math.max(0, ...axisKeys.map((_, index) => stacked.reduce((sum, item) => sum + (item.values[index] ?? 0), 0)));
    return {
      kind: "stacked_bar",
      across,
      axis: axisKeys.map((at, index) => ({ label: chart.time ? chartAxisLabel(at) : at, partial: chart.partialLast && index === axisKeys.length - 1 })),
      series: stacked,
      max: kindOfValues === "percent" ? 100 : chartScaleMax(largest),
      kindOfValues,
    };
  }

  const drawn = columns.map((item) => ({ key: item.key, label: item.label, kind: item.kind, values: table.rows.map((row) => numberOf(row[item.key])) }));
  const largest = Math.max(0, ...drawn.flatMap((item) => item.values.map((value) => value ?? 0)));
  return {
    kind: chart.kind,
    across,
    axis: table.rows.map((row, index) => ({ label: chart.time ? chartAxisLabel(row[x.key] ?? null) : String(row[x.key] ?? ""), partial: chart.partialLast && index === table.rows.length - 1 })),
    series: drawn,
    max: kindOfValues === "percent" ? 100 : chartScaleMax(largest),
    kindOfValues,
  };
}

/** The chart blocks of a turn with their tables: a chart draws the table right after it. */
export function assistantChartPairs<T extends { type: string }>(blocks: T[]): Map<number, number> {
  const pairs = new Map<number, number>();
  blocks.forEach((block, index) => {
    if (block.type === "chart" && blocks[index + 1]?.type === "table") pairs.set(index, index + 1);
  });
  return pairs;
}

// ---------------------------------------------------------------------------
// Old clients
// ---------------------------------------------------------------------------

/** The header in which a client names the optional block types it can draw: `X-Teamna-Blocks: chart`. */
export const ASSISTANT_BLOCKS_HEADER = "X-Teamna-Blocks";

/** The optional block types that the header names. An app from before charts sends no header. */
export function acceptedAssistantBlocks(header: string | null | undefined): AssistantOptionalBlockType[] {
  const named = (header ?? "").split(",").map((value) => value.trim().toLowerCase());
  return assistantOptionalBlockTypes.filter((type) => named.includes(type));
}

function shownTo(type: string, accepts: readonly string[]): boolean {
  return !(assistantOptionalBlockTypes as readonly string[]).includes(type) || accepts.includes(type);
}

/**
 * The blocks that a client can draw. An app from before charts draws a block type it does not know
 * as a proposal, so it must never get one: it gets the table that comes with each chart.
 */
export function assistantBlocksFor<T extends { type: string }>(blocks: T[], accepts: readonly string[]): T[] {
  return blocks.filter((block) => shownTo(block.type, accepts));
}

/** The index in the saved blocks of the block that a client saw at `shown` (More on a table). */
export function assistantSavedBlockIndex(blocks: Array<{ type: string }>, shown: number, accepts: readonly string[]): number {
  const saved = blocks.flatMap((block, index) => (shownTo(block.type, accepts) ? [index] : []));
  return saved[shown] ?? -1;
}

/**
 * What analytics learns about the generic data of one answer (event `assistant_turn_answered`):
 * the datasets of the query calls that worked, the chart kinds on screen, and the query calls that
 * failed. Names and counts only, never the spec or a value.
 */
export function assistantQueryAnalytics(
  calls: ReadonlyArray<{ name: string; input: Record<string, unknown>; ok: boolean }>,
  blocks: ReadonlyArray<{ type: string; kind?: unknown }>,
): { datasets: string[]; chart_kinds: AssistantChartKind[]; query_errors: number } {
  const queries = calls.filter((call) => call.name === "query");
  return {
    datasets: queries.flatMap((call) => (call.ok && typeof call.input.dataset === "string" ? [call.input.dataset] : [])),
    chart_kinds: blocks.flatMap((block) => (block.type === "chart" ? [(block as AssistantChartBlock).kind] : [])),
    query_errors: queries.filter((call) => !call.ok).length,
  };
}
